import * as React from 'react';
import { cn } from '@/lib/utils';
import { CDCheckbox } from '../primitives/CDCheckbox';
import { CDCurrency } from '../primitives/CDCurrency';
import { CDEditableText } from '../primitives/CDEditableText';
import { CDSectionHeader } from '../primitives/CDSectionHeader';

export interface PaymentPeriod {
  yearRange: string; // e.g., "Years 1-7", "Years 8-30"
  principalAndInterest: number;
  mortgageInsurance: number;
  estimatedEscrow: number;
  estimatedTotalMonthlyPayment?: number;
}

export interface EscrowItem {
  description: string;
  inEscrow: boolean;
}

export interface ProjectedPaymentsData {
  periods: PaymentPeriod[];
  estimatedTaxesInsuranceAssessments: number;
  escrowDescription?: string;
  inEscrow?: string[];
  notInEscrow?: string[];
}

export interface CDProjectedPaymentsProps {
  /** Projected payments data */
  data: Partial<ProjectedPaymentsData>;
  /** Whether fields are editable */
  editable?: boolean;
  /** onChange handler */
  onChange?: (data: Partial<ProjectedPaymentsData>) => void;
  /** Additional className */
  className?: string;
}

/**
 * CDProjectedPayments - Projected Payments section on Page 1 matching CFPB format
 *
 * Layout:
 * - Payment breakdown table with period columns
 * - "+" prefix for Mortgage Insurance and Estimated Escrow
 * - Estimated Taxes, Insurance & Assessments with escrow checklist
 */
export const CDProjectedPayments = React.forwardRef<HTMLDivElement, CDProjectedPaymentsProps>(
  ({ data, editable = false, onChange, className }, ref) => {
    const periods = data.periods || [];

    const calculateTotal = (period: PaymentPeriod) => {
      return (
        period.estimatedTotalMonthlyPayment ??
        period.principalAndInterest + period.mortgageInsurance + period.estimatedEscrow
      );
    };

    // Helper to update a specific period field
    const updatePeriod = (
      periodIndex: number,
      field: keyof PaymentPeriod,
      value: string | number,
    ) => {
      const updatedPeriods = periods.map((period, idx) => {
        if (idx === periodIndex) {
          return { ...period, [field]: value };
        }
        return period;
      });
      onChange?.({ ...data, periods: updatedPeriods });
    };

    // Helper to update estimated taxes
    const updateTaxes = (value: number) => {
      onChange?.({ ...data, estimatedTaxesInsuranceAssessments: value });
    };

    // Helper to toggle escrow status for an item
    const toggleEscrowItem = (item: string, currentlyInEscrow: boolean) => {
      if (currentlyInEscrow) {
        // Move from inEscrow to notInEscrow
        const newInEscrow = (data.inEscrow || []).filter((i) => i !== item);
        const newNotInEscrow = [...(data.notInEscrow || []), item];
        onChange?.({ ...data, inEscrow: newInEscrow, notInEscrow: newNotInEscrow });
      } else {
        // Move from notInEscrow to inEscrow
        const newNotInEscrow = (data.notInEscrow || []).filter((i) => i !== item);
        const newInEscrow = [...(data.inEscrow || []), item];
        onChange?.({ ...data, inEscrow: newInEscrow, notInEscrow: newNotInEscrow });
      }
    };

    return (
      <div ref={ref} className={cn('', className)}>
        <CDSectionHeader title="Projected Payments" />

        <div className="border border-t-0 border-cd-black">
          {/* Payment Calculation Table */}
          <div
            className="grid"
            style={{ gridTemplateColumns: `200px repeat(${periods.length}, 1fr)` }}
          >
            {/* Header Row */}
            <div className="p-2 border-b border-r border-cd-black cd-text-body font-bold">
              Payment Calculation
            </div>
            {periods.map((period, idx) => (
              <div
                key={idx}
                className={cn(
                  'p-2 text-center border-b border-cd-black cd-text-body font-bold',
                  idx < periods.length - 1 && 'border-r',
                )}
              >
                <CDEditableText
                  value={period.yearRange}
                  editable={editable}
                  onChange={(v) => updatePeriod(idx, 'yearRange', v)}
                  size="body"
                  bold
                  align="center"
                />
              </div>
            ))}

            {/* Principal & Interest Row */}
            <div className="p-2 border-b border-r border-cd-black cd-text-body pl-4">
              Principal & Interest
            </div>
            {periods.map((period, idx) => (
              <div
                key={idx}
                className={cn(
                  'p-2 text-right border-b border-cd-black cd-text-body',
                  idx < periods.length - 1 && 'border-r',
                )}
              >
                <CDCurrency
                  value={period.principalAndInterest}
                  editable={editable}
                  onChange={(v) => updatePeriod(idx, 'principalAndInterest', v)}
                />
              </div>
            ))}

            {/* Mortgage Insurance Row */}
            <div className="p-2 border-b border-r border-cd-black cd-text-body pl-4">
              Mortgage Insurance
            </div>
            {periods.map((period, idx) => (
              <div
                key={idx}
                className={cn(
                  'p-2 border-b border-cd-black cd-text-body',
                  idx < periods.length - 1 && 'border-r',
                )}
              >
                <div className="flex items-center justify-end gap-2">
                  <span className="font-bold">+</span>
                  {editable ? (
                    <CDCurrency
                      value={period.mortgageInsurance}
                      editable={true}
                      onChange={(v) => updatePeriod(idx, 'mortgageInsurance', v)}
                    />
                  ) : period.mortgageInsurance > 0 ? (
                    <CDCurrency value={period.mortgageInsurance} />
                  ) : (
                    <span className="text-right">&mdash;</span>
                  )}
                </div>
              </div>
            ))}

            {/* Estimated Escrow Row */}
            <div className="p-2 border-b border-r border-cd-black cd-text-body pl-4">
              Estimated Escrow
              <div className="cd-text-small text-cd-gray-text italic">
                Amount can increase over time
              </div>
            </div>
            {periods.map((period, idx) => (
              <div
                key={idx}
                className={cn(
                  'p-2 border-b border-cd-black cd-text-body',
                  idx < periods.length - 1 && 'border-r',
                )}
              >
                <div className="flex items-center justify-end gap-2">
                  <span className="font-bold">+</span>
                  <CDCurrency
                    value={period.estimatedEscrow}
                    editable={editable}
                    onChange={(v) => updatePeriod(idx, 'estimatedEscrow', v)}
                  />
                </div>
              </div>
            ))}

            {/* Estimated Total Monthly Payment Row */}
            <div className="p-2 border-b border-r border-cd-black cd-text-body font-bold">
              Estimated Total
              <br />
              Monthly Payment
            </div>
            {periods.map((period, idx) => (
              <div
                key={idx}
                className={cn(
                  'p-2 text-right border-b border-cd-black',
                  idx < periods.length - 1 && 'border-r',
                )}
              >
                <div className="border-t border-cd-black pt-1">
                  <CDCurrency value={calculateTotal(period)} className="text-[12pt] font-bold" />
                </div>
              </div>
            ))}
          </div>

          {/* Estimated Taxes, Insurance & Assessments Section */}
          <div className="p-2 grid grid-cols-[200px_100px_1fr] gap-2">
            <div>
              <div className="cd-text-body font-bold">
                Estimated Taxes, Insurance
                <br />& Assessments
              </div>
              <div className="cd-text-small text-cd-gray-text italic mt-1">
                Amount can increase over time
              </div>
              <div className="cd-text-small italic">See page 4 for details</div>
            </div>
            <div className="text-right">
              <CDCurrency
                value={data.estimatedTaxesInsuranceAssessments}
                editable={editable}
                onChange={updateTaxes}
                className="text-[12pt] font-bold"
              />
              <div className="cd-text-small">a month</div>
            </div>
            <div className="border-l border-cd-black pl-3">
              <div className="cd-text-body mb-1">
                <span className="font-bold">This estimate includes</span>
                <span className="float-right font-bold">In escrow?</span>
              </div>
              <div className="space-y-0.5 cd-text-body">
                {data.inEscrow?.map((item, idx) => (
                  <div key={idx} className="flex justify-between">
                    <span className="flex items-center gap-1">
                      <CDCheckbox
                        checked={true}
                        size="sm"
                        onChange={editable ? () => toggleEscrowItem(item, true) : undefined}
                      />
                      {item}
                    </span>
                    <span className="font-bold">YES</span>
                  </div>
                ))}
                {data.notInEscrow?.map((item, idx) => (
                  <div key={idx} className="flex justify-between">
                    <span className="flex items-center gap-1">
                      <CDCheckbox
                        checked={true}
                        size="sm"
                        onChange={editable ? () => toggleEscrowItem(item, false) : undefined}
                      />
                      Other: {item}
                    </span>
                    <span className="font-bold">NO</span>
                  </div>
                ))}
              </div>
              <div className="cd-text-small text-cd-gray-text mt-2">
                See Escrow Account on page 4 for details. You must pay for other property costs
                separately.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

CDProjectedPayments.displayName = 'CDProjectedPayments';
